/**
 * Esqueletos de carregamento.
 *
 * Substituem "Carregando seu perfil…" centralizado numa tela vazia. A
 * diferença não é enfeite: o texto solto não diz quanto falta nem o que
 * vem, então cada espera parece igual — a de 200ms e a de três segundos.
 * O esqueleto mostra a forma do que está chegando, e a tela não salta
 * quando o conteúdo entra, porque o espaço já estava reservado.
 *
 * Feito à mão em vez de biblioteca porque são vinte linhas de CSS. Uma
 * dependência para isto seria pagar peso de download por algo que o
 * navegador já sabe fazer.
 *
 * `prefers-reduced-motion` desliga a animação: brilho pulsando é
 * exatamente o tipo de movimento que incomoda quem pediu para não ter.
 */

function Barra({ largura, altura = 16 }: { largura: string; altura?: number }) {
  return (
    <span
      className="esqueleto block rounded"
      style={{ width: largura, height: altura }}
      aria-hidden="true"
    />
  );
}

/** Enquanto o perfil e o histórico não chegam. */
export function EsqueletoPerfil() {
  return (
    <main
      className="mx-auto w-full max-w-2xl px-4 py-8 grid gap-6 content-start grow"
      aria-busy="true"
      aria-label="Carregando seu perfil"
    >
      <Barra largura="9rem" altura={28} />

      <section className="card p-6 grid gap-4 justify-items-center">
        <span className="esqueleto rounded-full" style={{ width: 104, height: 104 }} />
        <Barra largura="7rem" altura={20} />
        <Barra largura="14rem" altura={12} />
      </section>

      <section className="card p-5 grid gap-5">
        <Barra largura="100%" altura={44} />
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: 8 }, (_unused, i) => (
            <span key={i} className="esqueleto rounded-full" style={{ width: 44, height: 44 }} />
          ))}
        </div>
      </section>
    </main>
  );
}

/** Enquanto a carteira não chega. */
export function EsqueletoCarteira() {
  return (
    <main
      className="mx-auto w-full max-w-2xl px-4 py-8 grid gap-6 content-start grow"
      aria-busy="true"
      aria-label="Abrindo a carteira"
    >
      <Barra largura="8rem" altura={28} />
      <section className="card p-5 flex items-baseline justify-between gap-4">
        <Barra largura="8rem" altura={12} />
        <Barra largura="7rem" altura={26} />
      </section>
      <section className="card p-5 grid gap-4">
        <Barra largura="10rem" altura={12} />
        <div className="flex flex-wrap gap-2">
          {Array.from({ length: 4 }, (_unused, i) => (
            <span key={i} className="esqueleto rounded-lg" style={{ width: 88, height: 40 }} />
          ))}
        </div>
        <Barra largura="100%" altura={48} />
      </section>
    </main>
  );
}

/** Enquanto o tabuleiro não está pronto. */
export function EsqueletoMesa() {
  return (
    <div
      className="mx-auto w-full max-w-5xl px-4 py-6 grid gap-6 lg:grid-cols-[1fr_20rem] items-start grow"
      aria-busy="true"
      aria-label="Preparando a mesa"
    >
      <span className="esqueleto rounded-xl w-full aspect-square" />
      <div className="grid gap-3">
        <Barra largura="100%" altura={52} />
        <Barra largura="100%" altura={52} />
        <div className="grid grid-cols-2 gap-2">
          <Barra largura="100%" altura={56} />
          <Barra largura="100%" altura={56} />
        </div>
      </div>
    </div>
  );
}
